import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './context/AuthContext';
import { apiGet, apiPost } from './api/client';
import { formatSavedPropertyLabel } from './utils/propertyLabel';

/** One-line hint from parseLvXml summary (hasUnit / stavba / pozemky) — not a legal classification. */
function lvKindLine(summary, t) {
  if (!summary || typeof summary !== 'object') return null;
  if (summary.hasUnit) {
    const vu = (summary.vyuziti || '').trim();
    return vu
      ? `${t('properties.lvKindUnit')} (${vu})`
      : t('properties.lvKindUnit');
  }
  if (summary.hasStavbaOrPozemky) {
    return t('properties.lvKindStavbaOrPozemky');
  }
  return null;
}

function PropertiesPage({
  onBack,
  onUseInRentOrder,
  onUseInSaleFlatOrder,
  onUseInReservationContract,
  isAdmin = false,
}) {
  const { t } = useTranslation();
  const { getToken } = useAuth();

  const [properties, setProperties] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState(null);
  const [listStatus, setListStatus] = useState(null);
  const [refreshingId, setRefreshingId] = useState(null);
  const [filterKu, setFilterKu] = useState('');
  const [filterLv, setFilterLv] = useState('');
  const [filterText, setFilterText] = useState('');

  // Load saved properties list
  const refreshProperties = async () => {
    setListLoading(true);
    setListError(null);
    setListStatus(null);
    try {
      const res = await apiGet(isAdmin ? '/api/properties?all=1' : '/api/properties', getToken);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || t('properties.errorLoad'));
      }
      if (Array.isArray(data.properties)) {
        setProperties(data.properties);
      } else {
        setProperties([]);
      }
    } catch (err) {
      setListError(err.message || t('properties.errorLoad'));
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    refreshProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredProperties = useMemo(() => {
    const kuDigits = filterKu.replace(/\D/g, '');
    const lvDigits = filterLv.replace(/\D/g, '');
    const q = filterText.trim().toLowerCase();
    return properties.filter((p) => {
      const pk = String(p.kod_ku ?? '').replace(/\D/g, '');
      const pl = String(p.cislo_lv ?? '').replace(/\D/g, '');
      if (kuDigits && pk !== kuDigits && !pk.startsWith(kuDigits)) return false;
      if (lvDigits && pl !== lvDigits && !pl.startsWith(lvDigits)) return false;
      if (q) {
        const who = isAdmin ? p.user_email || p.user_id : null;
        const label = formatSavedPropertyLabel(p, t, {
          adminEmailOrIdPrefix: who || undefined,
        }).toLowerCase();
        const clientName = (p.summary?.clientName && String(p.summary.clientName).toLowerCase()) || '';
        const c0 =
          Array.isArray(p.summary?.clients) && p.summary.clients[0]?.clientName != null
            ? String(p.summary.clients[0].clientName).toLowerCase()
            : '';
        const hay = `${label} ${clientName} ${c0}`.trim();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [properties, filterKu, filterLv, filterText, isAdmin, t]);

  const handleRefreshProperty = async (prop) => {
    try {
      const ok = window.confirm(t('properties.refreshWarning100'));
      if (!ok) return;
      setRefreshingId(prop.id);
      setListStatus(null);
      setListError(null);
      const res = await apiGet(
        `/api/ownership-xml?KodKU=${encodeURIComponent(prop.kod_ku)}&CisloLv=${encodeURIComponent(
          prop.cislo_lv,
        )}&refresh=1&confirm_paid_refresh=1${isAdmin && prop.user_id ? `&user_id=${encodeURIComponent(prop.user_id)}` : ''}`,
        getToken,
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.xml) {
        throw new Error(data.error || t('properties.errorRefresh'));
      }
      if (!(isAdmin && prop.user_id)) {
        await apiPost(
          '/api/properties',
          {
            kod_ku: prop.kod_ku,
            cislo_lv: prop.cislo_lv,
            xml: data.xml,
            summary: data.summary || null,
          },
          getToken,
        );
      }
      await refreshProperties();
      setListStatus(t('properties.refreshSuccess'));
    } catch (err) {
      setListError(err.message || t('properties.errorRefresh'));
    } finally {
      setRefreshingId(null);
    }
  };

  const handleUseInRentOrder = (prop) => {
    if (onUseInRentOrder) onUseInRentOrder(prop);
  };

  const handleUseInSaleFlatOrder = (prop) => {
    if (onUseInSaleFlatOrder) onUseInSaleFlatOrder(prop);
  };
  const handleUseInReservationContract = (prop) => {
    if (onUseInReservationContract) onUseInReservationContract(prop);
  };

  return (
    <div className="min-h-[60vh] flex flex-col items-stretch sm:items-center justify-start w-full max-w-full min-w-0 px-3 sm:px-4 py-6 sm:py-8">
      <div className="w-full max-w-5xl min-w-0">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="mb-6 text-sm text-slate-500 hover:text-slate-800 transition-colors"
          >
            ← {t('unitSearch.back')}
          </button>
        )}

        <div className="rounded-2xl border border-slate-200/80 bg-white shadow-lg shadow-slate-200/50 p-4 sm:p-6 min-w-0">
            <h1 className="text-lg font-semibold text-slate-800 tracking-tight mb-1">
              {t('properties.title')}
            </h1>
            <p className="text-slate-600 text-sm mb-4 leading-relaxed whitespace-pre-line">
              {t('properties.archiveIntro')}
            </p>
            <div className="mb-3">
              <h2 className="text-sm font-semibold text-slate-800 mb-2">
                {t('properties.listTitle')}
              </h2>
              <button
                type="button"
                onClick={refreshProperties}
                disabled={listLoading}
                className="text-left text-xs font-medium text-slate-600 hover:text-slate-900 disabled:opacity-50 disabled:cursor-not-allowed underline decoration-slate-300 underline-offset-2"
              >
                {t('properties.refreshList')}
              </button>
            </div>
            <div className="rounded-xl border border-slate-100 bg-slate-50/80 p-3 mb-4 space-y-2">
              <p className="text-[11px] text-slate-600">{t('properties.filterHint')}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <input
                  type="text"
                  inputMode="numeric"
                  value={filterKu}
                  onChange={(e) => setFilterKu(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder={t('properties.filterKuPlaceholder')}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm"
                  aria-label={t('properties.filterKuPlaceholder')}
                />
                <input
                  type="text"
                  inputMode="numeric"
                  value={filterLv}
                  onChange={(e) => setFilterLv(e.target.value.replace(/\D/g, '').slice(0, 8))}
                  placeholder={t('properties.filterLvPlaceholder')}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm"
                  aria-label={t('properties.filterLvPlaceholder')}
                />
              </div>
              <input
                type="search"
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                placeholder={t('properties.filterTextPlaceholder')}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm"
                aria-label={t('properties.filterTextPlaceholder')}
              />
            </div>
            {listError && (
              <div className="mb-3 rounded-xl bg-red-50 border border-red-100 px-3 py-2 text-xs text-red-700">
                {listError}
              </div>
            )}
            {listStatus && (
              <div className="mb-3 rounded-xl bg-emerald-50 border border-emerald-100 px-3 py-2 text-xs text-emerald-700">
                {listStatus}
              </div>
            )}
            {listLoading ? (
              <div className="text-xs text-slate-500">{t('properties.loading')}</div>
            ) : properties.length === 0 ? (
              <div className="text-xs text-slate-500">{t('properties.empty')}</div>
            ) : filteredProperties.length === 0 ? (
              <div className="text-xs text-slate-500">{t('properties.emptyFilter')}</div>
            ) : (
              <ul className="divide-y divide-slate-100 text-sm">
                {filteredProperties.map((p) => {
                  const kindHint = lvKindLine(p.summary, t);
                  const headline = formatSavedPropertyLabel(p, t, {
                    adminEmailOrIdPrefix: isAdmin ? p.user_email || p.user_id : undefined,
                  });
                  return (
                  <li key={p.id} className="py-4 flex flex-col gap-3">
                    <div>
                      <div className="font-medium text-slate-800">
                        {headline}
                      </div>
                      {isAdmin && p.user_email && (
                        <div className="text-xs text-slate-500">{p.user_email}</div>
                      )}
                      {p.summary?.clientName && (
                        <div className="text-slate-600">{p.summary.clientName}</div>
                      )}
                      {kindHint && (
                        <div className="text-xs text-slate-500 mt-0.5">
                          {t('properties.lvKindLabel')} {kindHint}
                        </div>
                      )}
                      {p.last_fetched_at && (
                        <div className="text-xs text-slate-400">
                          {t('properties.lastFetched')}{' '}
                          {new Date(p.last_fetched_at).toLocaleString()}
                        </div>
                      )}
                    </div>
                    <div className="rounded-xl border border-slate-100 bg-slate-50/80 p-3 space-y-3">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="flex flex-col gap-1.5">
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-indigo-900/70">
                            {t('properties.saleSection')}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleUseInSaleFlatOrder(p)}
                            disabled={!!refreshingId}
                            className="w-full px-3 py-2.5 rounded-lg bg-indigo-700 text-white text-xs font-semibold shadow-sm shadow-indigo-900/10 hover:bg-indigo-800 disabled:opacity-50 disabled:cursor-not-allowed text-center"
                          >
                            {t('properties.useInSaleFlatOrder')}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleUseInReservationContract(p)}
                            disabled={!!refreshingId}
                            className="w-full px-3 py-2.5 rounded-lg border border-indigo-300 text-indigo-700 text-xs font-semibold hover:bg-indigo-50 disabled:opacity-50 disabled:cursor-not-allowed text-center"
                          >
                            {t('properties.useInReservationFlat')}
                          </button>
                        </div>
                        <div className="flex flex-col gap-1.5">
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-900/70">
                            {t('properties.rentSection')}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleUseInRentOrder(p)}
                            disabled={!!refreshingId}
                            className="w-full px-3 py-2.5 rounded-lg bg-emerald-700 text-white text-xs font-semibold shadow-sm shadow-emerald-900/10 hover:bg-emerald-800 disabled:opacity-50 disabled:cursor-not-allowed text-center"
                          >
                            {t('properties.useInRentOrder')}
                          </button>
                          <button
                            type="button"
                            disabled
                            className="w-full px-3 py-2.5 rounded-lg border border-emerald-200 text-emerald-700 text-xs font-semibold opacity-70 cursor-not-allowed text-center"
                          >
                            {t('properties.useInRentHouseSoon')}
                          </button>
                        </div>
                      </div>
                      {isAdmin && (
                        <div className="pt-1 border-t border-slate-200/80">
                          <p className="text-[11px] text-amber-700 mb-1">{t('properties.refreshWarning100Short')}</p>
                          <button
                            type="button"
                            onClick={() => handleRefreshProperty(p)}
                            disabled={refreshingId === p.id}
                            className="w-full sm:w-auto sm:min-w-[10rem] px-3 py-2 rounded-lg border border-slate-200 bg-white text-slate-600 text-xs font-medium hover:bg-slate-50 hover:border-slate-300 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {t('properties.refreshButton')}
                          </button>
                        </div>
                      )}
                    </div>
                  </li>
                  );
                })}
              </ul>
            )}
        </div>
      </div>
    </div>
  );
}

export default PropertiesPage;

